const loading = new Map<string, Promise<void>>();

/** 同じ外部スクリプトを一度だけ読み込む。 */
export function loadScript(src: string): Promise<void> {
  const existing = loading.get(src);
  if (existing) return existing;

  const p = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(el);
  });
  loading.set(src, p);
  return p;
}

/* ---- YouTube IFrame Player API ---- */

export interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  setVolume(v: number): void;
  getVolume(): number;
  getPlayerState(): number;
  destroy(): void;
}

interface YTNamespace {
  Player: new (
    el: HTMLElement | string,
    opts: {
      events?: {
        onReady?: (e: { target: YTPlayer }) => void;
        onStateChange?: (e: { data: number; target: YTPlayer }) => void;
        onError?: (e: { data: number }) => void;
      };
    },
  ) => YTPlayer;
  PlayerState: { UNSTARTED: -1; ENDED: 0; PLAYING: 1; PAUSED: 2; BUFFERING: 3; CUED: 5 };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
    Twitch?: { Player: TwitchPlayerCtor };
  }
}

let ytReady: Promise<YTNamespace> | null = null;

export function loadYouTubeApi(): Promise<YTNamespace> {
  if (ytReady) return ytReady;
  ytReady = new Promise<YTNamespace>((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (window.YT) resolve(window.YT);
      else reject(new Error('YT namespace missing'));
    };
    loadScript('https://www.youtube.com/iframe_api').catch(reject);
  });
  return ytReady;
}

/* ---- Twitch Embed (Interactive Frames) ---- */

export interface TwitchPlayer {
  play(): void;
  pause(): void;
  setMuted(muted: boolean): void;
  getMuted(): boolean;
  setVolume(v: number): void;
  getVolume(): number;
  isPaused(): boolean;
  addEventListener(event: string, cb: () => void): void;
  destroy?: () => void;
}

type TwitchPlayerCtor = {
  new (
    el: HTMLElement | string,
    opts: {
      channel?: string;
      video?: string;
      parent: string[];
      width?: string | number;
      height?: string | number;
      autoplay?: boolean;
      muted?: boolean;
      playsinline?: boolean;
    },
  ): TwitchPlayer;
  READY: string;
  PLAY: string;
  PAUSE: string;
  ENDED: string;
  ONLINE: string;
  OFFLINE: string;
};

let twitchReady: Promise<TwitchPlayerCtor> | null = null;

export function loadTwitchApi(): Promise<TwitchPlayerCtor> {
  if (twitchReady) return twitchReady;
  twitchReady = loadScript('https://player.twitch.tv/js/embed/v1.js').then(() => {
    if (!window.Twitch?.Player) throw new Error('Twitch.Player missing');
    return window.Twitch.Player;
  });
  return twitchReady;
}
